import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  Search,
  MapPin,
  ChefHat,
  QrCode,
  Sparkles,
  Info,
  CheckCircle2,
  XCircle,
  Phone,
  Globe,
  Instagram,
  Utensils,
  ImageOff,
} from "lucide-react";
import { apiPublic } from "@/lib/api";

export default function PublicMenuPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const tableNum = searchParams.get("table");

  const [cafe, setCafe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [vegOnlyFilter, setVegOnlyFilter] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiPublic.get(`/public/menu/${slug}`)
      .then(({ data }) => {
        setCafe(data);
        setError(null);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || "Digital Menu Not Found");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Extract unique categories from menu items
  const categories = useMemo(() => {
    if (!cafe || !cafe.items) return ["All"];
    const cats = new Set(cafe.items.map((it) => it.category || "General"));
    return ["All", ...Array.from(cats)];
  }, [cafe]);

  // Filter items based on activeCategory, searchQuery, and vegOnlyFilter
  const filteredItems = useMemo(() => {
    if (!cafe || !cafe.items) return [];
    let list = cafe.items;

    if (activeCategory !== "All") {
      list = list.filter((it) => (it.category || "General") === activeCategory);
    }
    if (vegOnlyFilter) {
      list = list.filter((it) => it.is_veg);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (it) =>
          it.name.toLowerCase().includes(q) ||
          (it.description && it.description.toLowerCase().includes(q))
      );
    }
    return list;
  }, [cafe, activeCategory, searchQuery, vegOnlyFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center p-6 space-y-4">
        <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
        <p className="text-slate-400 text-sm font-mono">Loading Digital Menu...</p>
      </div>
    );
  }

  if (error || !cafe) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center p-6 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-red-500/10 text-red-500 grid place-items-center">
          <XCircle className="w-8 h-8" />
        </div>
        <h2 className="font-heading text-2xl font-bold">{error || "Menu Not Found"}</h2>
        <p className="text-slate-400 text-sm max-w-sm">The digital menu you are trying to access might have been updated or moved.</p>
        <Link to="/" className="px-6 py-2.5 rounded-full bg-primary text-white text-sm font-medium">
          Return to Tablezy
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-body pb-24">
      {/* 1. Cafe Cover & Header */}
      <header className="relative h-64 sm:h-80 bg-slate-900 overflow-hidden">
        <img
          src={cafe.cover_image_url || "https://images.unsplash.com/photo-1542372147193-a7aca54189cd?w=1200&auto=format&fit=crop"}
          alt={cafe.cafe_name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />

        {/* Table Number Pill Badge */}
        {tableNum && (
          <div className="absolute top-4 right-4 bg-primary text-white font-mono text-xs px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 border border-white/20">
            <QrCode className="w-3.5 h-3.5" />
            <span className="font-bold">Table #{tableNum}</span>
          </div>
        )}

        {/* Tablezy Powered Badge */}
        <div className="absolute top-4 left-4 bg-slate-900/80 backdrop-blur-md text-slate-300 text-[10px] font-mono px-3 py-1 rounded-full border border-white/10 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-primary" /> Powered by Tablezy
        </div>
      </header>

      {/* 2. Cafe Info Card */}
      <main className="max-w-3xl mx-auto px-4 -mt-20 relative z-10 space-y-6">
        <div className="bg-slate-900/90 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <img
                src={cafe.logo_url || "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=200&auto=format&fit=crop"}
                alt="Logo"
                className="w-16 h-16 rounded-2xl object-cover border-2 border-primary/30 shadow-md"
              />
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight">
                  {cafe.cafe_name}
                </h1>
                <p className="text-xs text-slate-400 flex items-center gap-1 mt-1 font-medium">
                  <MapPin className="w-3.5 h-3.5 text-primary" /> {cafe.location}
                </p>
              </div>
            </div>
          </div>

          <p className="text-slate-300 text-sm leading-relaxed">
            {cafe.description}
          </p>

          {/* Social / Contact links */}
          <div className="pt-3 border-t border-slate-800 flex flex-wrap items-center justify-between text-xs text-slate-400 gap-3">
            <div className="flex items-center gap-3">
              {cafe.mobile && (
                <a href={`tel:${cafe.mobile}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                  <Phone className="w-3.5 h-3.5 text-primary" /> {cafe.mobile}
                </a>
              )}
              {cafe.website_url && (
                <a href={cafe.website_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-white transition-colors">
                  <Globe className="w-3.5 h-3.5 text-primary" /> Website
                </a>
              )}
              {cafe.instagram_url && (
                <a href={cafe.instagram_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-white transition-colors">
                  <Instagram className="w-3.5 h-3.5 text-primary" /> Instagram
                </a>
              )}
            </div>
            <span className="text-[11px] font-mono text-emerald-400 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Digital Menu Verified
            </span>
          </div>
        </div>

        {/* 3. Search Bar & Filter Controls */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            {/* Search Input */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search dish, beverage or ingredient..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 pl-11 pr-4 rounded-2xl bg-slate-900 border border-slate-800 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
              />
            </div>

            {/* Veg Only Toggle */}
            <button
              onClick={() => setVegOnlyFilter(!vegOnlyFilter)}
              className={`h-12 px-4 rounded-2xl border text-xs font-semibold flex items-center gap-2 transition-all whitespace-nowrap ${
                vegOnlyFilter
                  ? "bg-emerald-500/20 border-emerald-500 text-emerald-400"
                  : "bg-slate-900 border-slate-800 text-slate-400 hover:text-white"
              }`}
            >
              <span>🟢 Veg Only</span>
            </button>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`h-10 px-5 rounded-full text-xs font-semibold whitespace-nowrap transition-all border ${
                  activeCategory === cat
                    ? "bg-primary text-white border-primary shadow-md shadow-primary/20"
                    : "bg-slate-900 text-slate-400 border-slate-800 hover:bg-slate-800 hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* 4. Menu Items Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between text-xs text-slate-400 font-mono">
            <span>SHOWING {filteredItems.length} DISHES</span>
            <span>PRICES INCL. TAXES</span>
          </div>

          {filteredItems.length === 0 ? (
            <div className="py-16 text-center bg-slate-900/60 border border-slate-800 rounded-3xl space-y-3">
              <ChefHat className="w-12 h-12 text-slate-600 mx-auto" />
              <h3 className="font-heading text-lg font-bold text-white">No Dishes Found</h3>
              <p className="text-slate-400 text-xs">Try searching for a different dish or category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredItems.map((item) => (
                <article
                  key={item.id}
                  className="bg-slate-900/80 border border-slate-800 rounded-3xl p-4 flex gap-4 hover:border-slate-700 transition-all"
                >
                  {/* Item Image */}
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-slate-950 overflow-hidden shrink-0 relative">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-slate-600">
                        <ImageOff className="w-6 h-6" />
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 ${
                              item.is_veg ? "border-emerald-500" : "border-red-500"
                            }`}
                          >
                            <span
                              className={`w-2 h-2 rounded-full ${
                                item.is_veg ? "bg-emerald-500" : "bg-red-500"
                              }`}
                            />
                          </span>
                          <h3 className="font-heading font-bold text-base sm:text-lg text-white leading-tight">
                            {item.name}
                          </h3>
                        </div>
                        <span className="font-mono text-base font-bold text-primary shrink-0">
                          ₹{item.price.toFixed(0)}
                        </span>
                      </div>

                      <p className="text-slate-400 text-xs mt-1.5 line-clamp-2 leading-relaxed">
                        {item.description}
                      </p>
                    </div>

                    <div className="mt-3 flex items-center justify-between text-[10px] font-mono">
                      <span className="bg-slate-800 text-slate-300 px-2.5 py-1 rounded-md">
                        {item.category || "General"}
                      </span>
                      {item.is_available !== false ? (
                        <span className="text-emerald-400 font-semibold">Available</span>
                      ) : (
                        <span className="text-slate-500">Sold Out</span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Sticky Bottom Bar */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-slate-900/90 backdrop-blur-xl border border-slate-700/80 rounded-full px-6 py-3 shadow-2xl flex items-center gap-3 text-xs text-white">
        <QrCode className="w-4 h-4 text-primary" />
        <span>Scanned at <b>{cafe.cafe_name}</b> {tableNum ? `(Table #${tableNum})` : ""}</span>
      </div>
    </div>
  );
}
